<template>
  <div class="subscription-plan-fields">
    <v-row>
      <v-col cols="12">
        <form-field
            type="select"
            :value="value.subscriptionPlan"
            :items="plans"
            :label="$tc('subscription.subscription-plan')"
            :rules="[commonRules.required]"
            :col-field="colField"
            :disabled="loading"
            item-text="name"
            return-object
            @input="choosePlan"
        />
      </v-col>
      <!-- Pour un profil d'équipement : la formule (seulement celles qui lui conviennent) et les options -->
      <template v-if="withFormula">
        <v-col cols="12">
          <form-field
              type="select"
              :value="value.formulaId"
              :items="formulas"
              :label="$t('onboarding.configuration.licence.formula')"
              :rules="[commonRules.required]"
              :col-field="colField"
              :disabled="!value.subscriptionPlan"
              item-text="name"
              item-value="id"
              @input="change({ formulaId: $event })"
          />
        </v-col>
        <v-col v-if="options.length" cols="12">
          <form-field
              type="select"
              :value="value.optionIds"
              :items="options"
              :label="$t('onboarding.configuration.licence.options')"
              :col-field="colField"
              item-text="name"
              item-value="id"
              multiple chips
              @input="change({ optionIds: $event || [] })"
          />
        </v-col>
      </template>
      <v-col cols="12" :md="compact ? 12 : 6">
        <form-field
            type="select"
            :value="value.billingCycle"
            :items="billingCycles"
            :label="$tc('subscription.billing-cycle')"
            :rules="[commonRules.required]"
            :col-field="colField"
            @input="change({ billingCycle: $event })"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" :md="compact ? 12 : 6">
        <form-field
            type="date"
            :value="value.activationStartDate"
            :label="$tc('subscription.activation-start-date')"
            :rules="[commonRules.required]"
            :col-field="colField"
            @input="change({ activationStartDate: $event })"
        />
      </v-col>
      <v-col cols="12" :md="compact ? 12 : 6">
        <form-field
            type="date"
            :value="value.billingStartDate"
            :label="$tc('subscription.billing-start-date')"
            :rules="[commonRules.required]"
            :col-field="colField"
            @input="change({ billingStartDate: $event })"
        />
      </v-col>
    </v-row>

    <!-- Les réglages du modèle (dont ceux du remboursement) -->
    <v-row v-if="value.subscriptionPlan && value.subscriptionPlan.customFieldsDefinition">
      <v-col>
        <custom-fields
            :value="value.customFields"
            :fields="value.subscriptionPlan.customFieldsDefinition"
            :col-field="colField"
            @input="change({ customFields: $event })"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import CustomFields from '@blocks/CustomFields.vue'
import RulesMixin from '@mixins/RulesMixin'

/** Un abonnement à saisir : aujourd'hui pour les deux dates, au mois tant que le modèle ne dit pas autre chose. */
export function blankSubscription() {
  return {
    subscriptionPlan: null,
    formulaId: null,
    optionIds: [],
    billingCycle: 'MONTHLY',
    activationStartDate: dayjs().format('YYYY-MM-DD'),
    billingStartDate: dayjs().format('YYYY-MM-DD'),
    customFields: [],
  }
}

/** Ce que le serveur attend pour souscrire : le modèle par son identifiant, le reste tel quel. */
export function subscriptionBody(value, withFormula = false) {
  const body = {
    subscriptionPlanId: value.subscriptionPlan?.id || null,
    billingCycle: value.billingCycle,
    activationStartDate: value.activationStartDate,
    billingStartDate: value.billingStartDate,
    customFields: value.customFields || [],
  }
  if (withFormula) {
    body.formulaId = value.formulaId
    body.optionIds = value.optionIds || []
  }
  return body
}

/**
 * Les champs d'un nouvel abonnement d'organisation : le modèle, son cycle de facturation, ses dates, ses réglages ;
 * pour un profil d'équipement, aussi la formule et les options. Partagé par la souscription depuis la fiche
 * organisation et par l'éditeur de profil (abonnement saisi sur place).
 */
export default {
  name: 'SubscriptionPlanFields',

  components: { CustomFields },

  mixins: [RulesMixin],

  props: {
    /** { subscriptionPlan, formulaId, optionIds, billingCycle, activationStartDate, billingStartDate, customFields } */
    value: { type: Object, required: true },
    /** Les modèles proposés ; avec `eligibleFormulaIds`, seules ces formules se choisissent. */
    plans: { type: Array, default: () => [] },
    /** Demander la formule et les options (un profil d'équipement). */
    withFormula: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    colField: { type: [Number, String], default: 9 },
    /** Dans un panneau étroit : un champ par ligne. */
    compact: { type: Boolean, default: false },
  },

  computed: {
    billingCycles() {
      const types = this.$t('enums.billing-cycle')
      return Object.keys(types).map(type => ({ text: types[type], value: type }))
    },

    formulas() {
      const plan = this.value.subscriptionPlan
      if (!plan) return []
      const eligible = plan.eligibleFormulaIds
      return (plan.formulas || []).filter(f => f.enabled !== false && (!eligible || eligible.includes(f.id)))
    },

    options() {
      return (this.value.subscriptionPlan?.options || []).filter(o => o.enabled !== false)
    },
  },

  methods: {
    change(patch) {
      this.$emit('input', { ...this.value, ...patch })
    },

    /** Un autre modèle : ses réglages, son cycle par défaut ; sa seule formule possible, déjà choisie. */
    choosePlan(plan) {
      const formulas = plan ? (plan.formulas || []).filter(f => f.enabled !== false && (!plan.eligibleFormulaIds || plan.eligibleFormulaIds.includes(f.id))) : []
      this.change({
        subscriptionPlan: plan || null,
        customFields: plan?.customFields ? this.cloneObj(plan.customFields) : [],
        billingCycle: plan?.defaultBillingPeriod ?? 'MONTHLY',
        formulaId: formulas.length === 1 ? formulas[0].id : null,
        optionIds: [],
      })
    },
  },
}
</script>
